'use client';
import { crudNotFound } from '@bells/i18n';
import { FileSearch } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useMemo, useRef } from 'react';
import {
  AttendantItem,
  AttendantItemPlaceholder,
  BecomeAttendantPlaceholder,
} from '../../components/attendant-item';
import { PaginationWithLinks } from '../../components/pagination-with-links';
import { useFilter } from './filter-provider';
import { usePwaRouter } from '../../utils/use-pwa';
import { cn } from '@bells/core/lib/utils';

export const AttendantsList = ({
  prefixUrl,
  className,
  withAdvert = true,
}: {
  prefixUrl?: string;
  className?: string;
  withAdvert?: boolean;
}) => {
  const isPwa = usePwaRouter();
  const { data, isLoading, isPlaceholderData, setFilters } = useFilter();
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  const items = useMemo(() => {
    return data?.body.items.map((df, index) => {
      const element = isPlaceholderData ? (
        <AttendantItemPlaceholder key={df.id} />
      ) : (
        <AttendantItem
          prefixUrl={prefixUrl}
          key={df.id}
          preLoad={index === 0}
          item={df}
        />
      );

      if (index === 0 && withAdvert) {
        return [element, <BecomeAttendantPlaceholder key='become-attendant' />];
      }

      return element;
    });
  }, [data?.body.items, isPlaceholderData, withAdvert]);

  return (
    <div className={cn('flex w-full flex-col gap-4', className)} ref={ref}>
      {(!data || data.body.items?.length <= 0) && !isLoading && (
        <div className='flex w-full flex-col items-center gap-2 text-center'>
          <div className='flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl bg-yellow-300 text-yellow-100'>
            <FileSearch className='h-[70%] w-[70%]'></FileSearch>
          </div>
          <span className='text-base font-medium'>{crudNotFound()}</span>
        </div>
      )}

      <div
        className={cn('grid grid-cols-1 gap-4 sm:grid-cols-2', {
          'lg:grid-cols-3 xl:grid-cols-4': !isPwa,
        })}
      >
        {items}
      </div>

      <div className='w-full'>
        {data?.body.meta && (
          <PaginationWithLinks
            url={pathname}
            onChange={(e, _, page) => {
              e.preventDefault();
              e.stopPropagation();
              e.nativeEvent.stopImmediatePropagation();

              setFilters((prev) => ({ ...prev, page: page }));

              const elm = ref.current;

              if (elm && typeof window !== 'undefined') {
                const scrollTarget = elm.offsetTop - 75;
                requestAnimationFrame(() => {
                  window.scrollTo({
                    top: scrollTarget,
                    left: 0,
                    behavior: 'smooth',
                  });
                });
              }
            }}
            useQuery={true}
            className='mt-8'
            totalCount={data?.body.meta.totalItems}
            pageSize={data?.body.meta.limit}
            page={data?.body.meta.currentPage ?? 1}
          ></PaginationWithLinks>
        )}
      </div>
    </div>
  );
};
